import React, { useState, useRef } from 'react';
import { Button, Form, ProgressBar, Alert, Modal } from 'react-bootstrap';
import { FaUpload, FaFile, FaTimes, FaCheck } from 'react-icons/fa';
import DataApi from '../api/DataApi';

const Attachment = ({ parentId, onUploadSuccess, onClose }) => {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [uploadResults, setUploadResults] = useState([]);
  const [description, setDescription] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  // Handle file selection
  const handleFileSelect = (files) => {
    const fileArray = Array.from(files);
    const validFiles = fileArray.filter(file => {
      // Check file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        alert(`File ${file.name} is too large. Maximum size is 10MB.`);
        return false;
      }
      return true;
    });

    setSelectedFiles(prev => [...prev, ...validFiles]);
  };

  // Handle drag and drop
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files);
    }
  };

  // Remove file from selection
  const removeFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Get file icon based on type
  const getFileIcon = (fileType) => {
    if (fileType.includes('image')) return '🖼️';
    if (fileType.includes('pdf')) return '📄';
    if (fileType.includes('word')) return '📝';
    if (fileType.includes('excel') || fileType.includes('spreadsheet')) return '📊';
    if (fileType.includes('zip') || fileType.includes('rar')) return '📦';
    return '📎';
  };

  // Upload files
  const handleUpload = async () => {
    if (selectedFiles.length === 0) {
      alert('Please select at least one file to upload.');
      return;
    }

    setUploading(true);
    setUploadResults([]);
    setUploadProgress({});

    try {
      const formData = new FormData();
      
      // Add files to FormData - use 'files' as the field name for multer
      selectedFiles.forEach((file) => {
        formData.append('files', file);
      });
      
      // Add description
      if (description.trim()) {
        formData.append('description', description.trim());
      }

      // Simulate progress for each file
      selectedFiles.forEach((_, index) => {
        setUploadProgress(prev => ({
          ...prev,
          [index]: 0
        }));
      });

      // Upload files using DataApi
      const dataApi = new DataApi('attachments');
      const response = await dataApi.post(`/${parentId}`, formData);

      if (response.data && response.data.success) {
        setUploadResults(response.data.data || []);
        
        // Show success message
        setTimeout(() => {
          if (onUploadSuccess) {
            onUploadSuccess(response.data.data);
          }
          // Reset form
          setSelectedFiles([]);
          setDescription('');
          setUploadProgress({});
          setUploadResults([]);
        }, 2000);
      } else {
        throw new Error(response.data?.message || 'Upload failed');
      }

    } catch (error) {
      console.error('Upload error:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Upload failed';
      alert(`Upload failed: ${errorMessage}`);
    } finally {
      setUploading(false);
    }
  };

  return (
    <Modal show={true} onHide={onClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <FaUpload className="me-2" />
          Upload Attachments
        </Modal.Title>
      </Modal.Header>
      
      <Modal.Body>
        {/* Description Field */}
        <Form.Group className="mb-3">
          <Form.Label>Description (Optional)</Form.Label>
          <Form.Control
            as="textarea"
            rows={2}
            placeholder="Enter a description for these files..."
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            disabled={uploading}
          />
        </Form.Group>

        {/* File Upload Area */}
        <div
          className={`upload-area ${dragActive ? 'drag-active' : ''} ${uploading ? 'uploading' : ''}`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={() => !uploading && fileInputRef.current?.click()}
          style={{
            border: '2px dashed #ccc',
            borderRadius: '8px',
            padding: '40px 20px',
            textAlign: 'center',
            cursor: uploading ? 'not-allowed' : 'pointer',
            backgroundColor: dragActive ? '#f8f9fa' : '#fff',
            transition: 'all 0.3s ease'
          }}
        >
          <input
            ref={fileInputRef}
            type="file"
            multiple
            style={{ display: 'none' }}
            onChange={(e) => handleFileSelect(e.target.files)}
            disabled={uploading}
          />
          
          {uploading ? (
            <div>
              <div className="spinner-border text-primary mb-3" role="status">
                <span className="visually-hidden">Uploading...</span>
              </div>
              <p className="text-muted">Uploading files...</p>
            </div>
          ) : (
            <div>
              <FaUpload size={48} className="text-muted mb-3" />
              <h5>Drop files here or click to browse</h5>
              <p className="text-muted">Maximum file size: 10MB</p>
            </div>
          )}
        </div>

        {/* Selected Files */}
        {selectedFiles.length > 0 && (
          <div className="mt-3">
            <h6>Selected Files ({selectedFiles.length})</h6>
            <div className="selected-files">
              {selectedFiles.map((file, index) => (
                <div key={index} className="file-item d-flex align-items-center justify-content-between p-2 border rounded mb-2">
                  <div className="d-flex align-items-center">
                    <span className="me-2" style={{ fontSize: '1.2em' }}>
                      {getFileIcon(file.type)}
                    </span>
                    <div>
                      <div className="fw-medium">{file.name}</div>
                      <small className="text-muted">{formatFileSize(file.size)}</small>
                    </div>
                  </div>
                  
                  <div className="d-flex align-items-center">
                    {/* Upload Progress */}
                    {uploading && uploadProgress[index] !== undefined && (
                      <div className="me-2" style={{ width: '100px' }}>
                        <ProgressBar 
                          now={uploadProgress[index]} 
                          size="sm"
                          variant={uploadProgress[index] === 100 ? 'success' : 'primary'}
                        />
                      </div>
                    )}
                    
                    {/* Upload Result */}
                    {uploadResults[index] && (
                      <FaCheck className="text-success me-2" />
                    )}
                    
                    {/* Remove Button */}
                    {!uploading && (
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => removeFile(index)}
                      >
                        <FaTimes />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Upload Results */}
        {uploadResults.length > 0 && (
          <Alert variant="success" className="mt-3">
            <FaCheck className="me-2" />
            Successfully uploaded {uploadResults.length} file(s)!
          </Alert>
        )}
      </Modal.Body>
      
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose} disabled={uploading}>
          Cancel
        </Button>
        <Button 
          variant="primary" 
          onClick={handleUpload}
          disabled={selectedFiles.length === 0 || uploading}
        >
          {uploading ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              Uploading...
            </>
          ) : (
            <>
              <FaUpload className="me-2" />
              Upload {selectedFiles.length} File{selectedFiles.length !== 1 ? 's' : ''}
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default Attachment;
